/**
 * Pick Season Summary pack from the importer_report season bundle (Ask #54/#55).
 * No USA soft-default: All uses packs.ALL when published.
 * Packs are keyed by scope token (`USA`, `USA__PERU`, `CANADA--USA`); a scope
 * that was not published is the 'missing' state, never a dest/origin error.
 */
import { resolveSeasonDestKey } from './seasonDestination.js';
import { resolveSeasonOriginKey } from './seasonOrigin.js';
import { buildSeasonScope } from './seasonScope.js';

/**
 * @param {object|null|undefined} seasonBundle response of importer_report_preview bundle=season
 * @param {{ mode: string, keys: Set<string> }|null|undefined} destSel
 * @param {string} seasonCommodity resolveSeasonCommodity(...)
 * @param {{ mode: string, keys: Set<string> }|null|undefined} [originSel]
 * @returns {{
 *   seasonAccess: string|null,
 *   pack: object|null,
 *   destKey: string,
 *   originKey: string,
 *   scope: string,
 *   destMissing: boolean,
 *   originMissing: boolean,
 *   status: string|null,
 *   codes: string[],
 * }}
 */
export function selectSeasonFromActive(
  seasonBundle,
  destSel,
  seasonCommodity,
  originSel = null,
) {
  const { key: destKey, fetchable, codes } = resolveSeasonDestKey(destSel);
  const origin = resolveSeasonOriginKey(originSel);
  const seasonAccess =
    seasonBundle && typeof seasonBundle.season_access === 'string'
      ? seasonBundle.season_access
      : null;
  const status =
    seasonBundle && typeof seasonBundle.status === 'string'
      ? seasonBundle.status
      : null;

  const scope = fetchable && origin.supported ? buildSeasonScope(destKey, origin.key) : '';

  const empty = (extra = {}) => ({
    seasonAccess,
    pack: null,
    destKey,
    originKey: origin.key,
    scope,
    destMissing: true,
    originMissing: !origin.supported,
    status,
    codes,
    ...extra,
  });

  if (!seasonCommodity) {
    return {
      seasonAccess,
      pack: null,
      destKey,
      originKey: origin.key,
      scope,
      destMissing: false,
      originMissing: false,
      status,
      codes,
    };
  }

  // Origin selection with no Peru/Chile has nothing to publish.
  if (!origin.supported) {
    return empty({ destMissing: false, originMissing: true });
  }

  // Nothing selected for destination.
  if (destKey === 'NONE' || !fetchable) {
    return empty({ originMissing: false });
  }

  const packs = seasonBundle?.packs;
  if (!packs || typeof packs !== 'object') {
    // Keep wire status (ready/unavailable/hidden); do not invent unavailable.
    return empty({
      destMissing: true,
      originMissing: false,
    });
  }

  const destPacks = scope ? packs[scope] : null;
  if (!destPacks || typeof destPacks !== 'object') {
    return empty({ originMissing: false });
  }

  const pack = destPacks[seasonCommodity];
  if (pack == null || typeof pack !== 'object') {
    return empty({ originMissing: false });
  }

  return {
    seasonAccess,
    pack,
    destKey,
    originKey: origin.key,
    scope,
    destMissing: false,
    originMissing: false,
    status,
    codes,
  };
}

/**
 * State of the live Season call (single-cell preview) as the hook sees it.
 * `empty` = the backend found no volume (`empty: true`, or no preview object).
 *
 * @param {{ active: boolean, isLoading?: boolean, isError?: boolean, data?: object|null }} args
 * @returns {{ pack: object|null, status: 'idle'|'loading'|'ready'|'empty'|'error' }}
 */
export function resolveSeasonLivePack({ active, isLoading = false, isError = false, data = null }) {
  if (!active) return { pack: null, status: 'idle' };
  if (isLoading) return { pack: null, status: 'loading' };
  if (isError) return { pack: null, status: 'error' };
  if (!data || typeof data !== 'object') return { pack: null, status: 'idle' };
  const preview = data.preview;
  if (data.empty === true || !preview || typeof preview !== 'object') {
    return { pack: null, status: 'empty' };
  }
  return {
    pack: { ...preview, generated_at: preview.generated_at || data.generated_at },
    status: 'ready',
  };
}

/**
 * Merge one live pack for `scope` x `commodity` into the published bundle's
 * packs. Pure; a published pack wins over the live one. Without a bundle
 * (access unknown) or a pack nothing is merged.
 */
export function mergeSeasonLivePack(seasonBundle, scope, commodity, pack) {
  if (!seasonBundle || typeof seasonBundle !== 'object') return seasonBundle;
  if (!scope || !commodity || pack == null || typeof pack !== 'object') return seasonBundle;
  const base = seasonBundle.packs && typeof seasonBundle.packs === 'object' ? seasonBundle.packs : {};
  const scoped = base[scope] && typeof base[scope] === 'object' ? base[scope] : {};
  // A published null (no row) must not hide the live pack.
  if (scoped[commodity] != null) return seasonBundle;
  return {
    ...seasonBundle,
    packs: { ...base, [scope]: { ...scoped, [commodity]: pack } },
  };
}

/**
 * Single source of truth for the Season Summary chrome (cards + modal).
 * loading only while the bundle request is in flight and no bundle exists;
 * a settled request without a bundle resolves to unavailable.
 * A cell with no pack follows the live call: loading, empty (no volume),
 * error, else missing.
 *
 * @param {{
 *   seasonBundle: object|null|undefined,
 *   seasonBundleLoading?: boolean,
 *   seasonBundleError?: boolean,
 *   selected: ReturnType<typeof selectSeasonFromActive>,
 *   destKey?: string,
 *   liveStatus?: 'idle'|'loading'|'ready'|'empty'|'error',
 * }} args
 * @returns {'loading'|'empty'|'error'|'unavailable'|'hidden'|'metrics'|'teaser'|'origin_unsupported'|'dest_unsupported'|'missing'|'ready'}
 */
export function resolveSeasonChromeState({
  seasonBundle,
  seasonBundleLoading = false,
  seasonBundleError = false,
  selected,
  destKey = '',
  liveStatus = 'idle',
}) {
  if (seasonBundleLoading && !seasonBundle) return 'loading';
  if (seasonBundleError) return 'error';
  if (!seasonBundle || selected.status === 'unavailable') return 'unavailable';
  if (selected.seasonAccess === 'hide' || selected.status === 'hidden') return 'hidden';
  if (selected.originMissing) return 'origin_unsupported';
  if (destKey === 'NONE') return 'dest_unsupported';
  if (selected.seasonAccess === 'metrics' || selected.status === 'metrics') {
    // metrics = locked teaser: the live preview returns variety names only.
    if (selected.pack?.locked === true) return 'teaser';
    if (liveStatus === 'loading') return 'loading';
    return 'metrics';
  }
  if (selected.destMissing || selected.pack == null) {
    if (liveStatus === 'loading') return 'loading';
    if (liveStatus === 'empty') return 'empty';
    if (liveStatus === 'error') return 'error';
    return 'missing';
  }
  return 'ready';
}

/**
 * Why the Download button is disabled, or null when it may be used.
 * Returns the copy key suffix under map.scorecards.
 * @returns {string|null}
 */
export function seasonPdfDisabledReason(state, destKey, pack) {
  if (state === 'ready') return pack?.pdf_available === false ? 'pdfNotPublished' : null;
  if (state === 'loading') return null;
  if (state === 'dest_unsupported') {
    return 'seasonSelectDestination';
  }
  if (state === 'origin_unsupported') return 'seasonOriginNotPublished';
  if (state === 'missing') return 'seasonScopeNotPublished';
  if (state === 'empty') return 'seasonEmpty';
  return 'pdfNotPublished';
}
